import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  DatePicker,
  Select,
  Space,
  Tooltip,
  Switch,
  Typography,
  Badge,
  Avatar,
  Image,
  Divider,
} from "antd";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import {
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  CheckOutlined,
  SafetyOutlined,
  FilterFilled,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import lang from "../helper/lang";
import { Eye, Pencil, Trash2, Lock } from "lucide-react";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SuspendIcon from "../assets/images/suspend-main-icon.svg";
import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import SingleImageUpload from "../components/SingleImageUpload";
import notfound from "../assets/images/not_found.png";
import PhoneInput from "react-phone-input-2";
import moment from "moment";
import { toast } from "react-toastify";
import RolePermissionsModal from "../components/RolePermissionModal";
import {
  MINIMUM_AGE,
  getMaximumDobByAge,
  createMinimumAgeValidator,
} from "../helper/validation";

const { Search } = Input;
const { Title, Text } = Typography;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const PERMISSION_ICONS = {
  View: <EyeOutlined />,
  Create: <PlusOutlined />,
  Edit: <EditOutlined />,
  Delete: <DeleteOutlined />,
};

function SubAdminManager() {
  const { request } = useRequest();
  const maxAllowedDob = getMaximumDobByAge(MINIMUM_AGE);
  const validateMinimumAge = createMinimumAgeValidator(MINIMUM_AGE, "User");

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [image, setImage] = useState();
  const [modalType, setModalType] = useState("add");
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const FileType = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
  const [filters, setFilters] = useState({
    status: null,
    role: null,
    sort: null,
    dateRange: null,
  });
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusToggleLoading, setStatusToggleLoading] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const debouncedSearchText = useDebounce(searchText, 500);
  const isInitialRender = useRef(true);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);

  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Helper function to map API status to boolean
  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  const fetchRoles = useCallback(() => {
    setRolesLoading(true);
    request({
      url: "/api/admin/roles",
      method: "GET",
      onSuccess: (res) => {
        setRoles(res.data || []);
        setRolesLoading(false);
      },
      onError: () => {
        setRoles([]);
        setRolesLoading(false);
      },
    });
  }, [request]);

  // Fetch users
  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status === true) {
      params.append("status", "Active");
    } else if (filters.status === false) {
      params.append("status", "Inactive");
    } else {
      params.append("status", "");
    }
    if (filters.sort) {
      params.append("sort", filters.sort);
    }
    if (filters.role) {
      params.append("role_id", filters.role);
    }

    if (filters.dateRange?.length === 2) {
      try {
        // RangePicker returns dayjs objects which have format method
        params.append("start_date", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("end_date", filters.dateRange[1].format("YYYY-MM-DD"));
      } catch (e) {
        console.error("Date formatting error:", e);
        // Fallback: try to get date string in different ways
        try {
          if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toISOString === "function"
          ) {
            params.append(
              "start_date",
              filters.dateRange[0].toISOString().split("T")[0],
            );
            params.append(
              "end_date",
              filters.dateRange[1].toISOString().split("T")[0],
            );
          } else if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toDate === "function"
          ) {
            // If it's a dayjs object with toDate method
            params.append(
              "startDate",
              filters.dateRange[0].toDate().toISOString().split("T")[0],
            );
            params.append(
              "endDate",
              filters.dateRange[1].toDate().toISOString().split("T")[0],
            );
          }
        } catch (fallbackError) {
          console.error("Fallback date formatting also failed:", fallbackError);
        }
      }
    }
    if (debouncedSearchText) params.append("search", debouncedSearchText);
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `/api/admin/sub-admins?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let usersData = [];
        let totalCount = 0;
        let currentPageFromApi = undefined;
        let limitFromApi = undefined;

        if (data?.data) {
          // Common shapes: { data: { users, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            usersData = data.data;
          }

          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          usersData = data;
          totalCount = data.length;
        }

        let normalizedUsers = Array.isArray(usersData) ? [...usersData] : [];

        // Fallback local sorting to keep UI sorting reliable
        // even if backend sort key mapping differs.
        if (filters.sort === "name") {
          normalizedUsers.sort((a, b) =>
            String(a?.full_name || "")
              .toLowerCase()
              .localeCompare(String(b?.full_name || "").toLowerCase()),
          );
        } else if (filters.sort === "status") {
          normalizedUsers.sort((a, b) =>
            String(a?.status || "")
              .toLowerCase()
              .localeCompare(String(b?.status || "").toLowerCase()),
          );
        } else if (filters.sort === "newest") {
          normalizedUsers.sort(
            (a, b) =>
              new Date(b?.join_date || b?.created_at || 0).getTime() -
              new Date(a?.join_date || a?.created_at || 0).getTime(),
          );
        } else if (filters.sort === "oldest") {
          normalizedUsers.sort(
            (a, b) =>
              new Date(a?.join_date || a?.created_at || 0).getTime() -
              new Date(b?.join_date || b?.created_at || 0).getTime(),
          );
        }

        setUsers(normalizedUsers);
        // Keep current/pageSize from UI state to avoid double-fetch loops
        // when API returns same values with different types (e.g. "1" vs 1).
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setUsers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast(
          `${error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
          }`,
          Severty.ERROR,
        );
      },
    });
  }, [
    filters,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
    request,
  ]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (searchText === "" && debouncedSearchText !== "") {
      return;
    }
    fetchUsers();
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleRemoveImage = () => {
    setImage(null); // clear image preview
    form.setFieldsValue({ image: null }); // clear form field
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddUser = () => {
    setModalType("add");
    form.resetFields();

    // 🔥 reset phone state
    setPhoneCountry({ dialCode: "64" });
    setPhoneCountryIso("NZ");
    setCountryCode("64");

    setImage(null);
    setModalVisible(true);
  };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;

    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase(),
    );

    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    if (countryCode === "NZ") {
      return "+64 xxx xxx xx"; // ✅ clean & correct NZ format
    }

    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;

      const intl = example.formatInternational();

      let seenCountryCode = false;

      return intl.replace(/\d+/g, (match) => {
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match;
        }
        return "x".repeat(Math.max(match.length - 1, 1));
      });
    } catch {
      return null;
    }
  };

  const handleEditUser = (user) => {
    const dialCode = String(user?.country_code || "").replace(/\D/g, "") || "64";
    const mobileRaw = String(user?.mobile || user?.phone || "").replace(/\D/g, "");
    const localMobile = mobileRaw.startsWith(dialCode)
      ? mobileRaw.slice(dialCode.length)
      : mobileRaw;
    const fullPhone = `${dialCode}${localMobile}`;

    setSelectedUser(user);
    setModalType("edit");
    setModalVisible(true);
    form.setFieldsValue({
      first_name: user.first_name,
      middle_name: user.middle_name,
      last_name: user.last_name,
      username: user.username || "",
      email: user.email,
      gender: user.gender,
      role_id: String(user.role.id),
      mobile: fullPhone,
      isActive: user.status === "Active" ? true : false,
      pronoun: user.pronoun,
      blood_group: user.blood_group,
      date_of_birth: user.date_of_birth ? moment(user.date_of_birth) : null,
    });
    setCountryCode(dialCode);
    setPhoneCountryIso(dialCode === "64" ? "NZ" : null);
    setPhoneCountry({ dialCode });
    setImage(user.profile_img || null);
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setModalType("view");
    setModalVisible(true);
  };

  // const handleViewRole = (role) => {
  //   setSelectedRole(role);
  //   setRoleModalVisible(true);
  // };

  const handleImage = (data) => {
    data.length > 0 ? setImage(data?.[0]?.url) : setImage("");
  };

  const PRONOUN_OPTIONS = ["Dr.", "Mr.", "Ms.", "Mrs."].map((pr) => ({
    label: pr,
    value: pr,
  }));

  const GENDER_OPTIONS = [
    { label: "Male", value: "MALE" },
    { label: "Female", value: "FEMALE" },
    { label: "Other", value: "OTHERS" },
    { label: "Prefer not to say", value: "PREFER_NOT_TO_SAY" },
  ];

  const BLOOD_GROUP_OPTIONS = [
    "A+",
    "A-",
    "B+",
    "B-",
    "AB+",
    "AB-",
    "O+",
    "O-",
  ].map((bg) => ({ label: bg, value: bg }));

  //   ------------ USER Create and Update ----------
  const handleFormSubmit = async (values) => {
    try {
      const userData = {
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        email: values.email,
        mobile: values.mobile.slice(countryCode.length),
        country_code: countryCode,
        profile_img: image,
        status: values.isActive ? "Active" : "Inactive",
        pronoun: values.pronoun ?? null,
        gender: values.gender ?? form.getFieldValue("gender") ?? null,
        blood_group: values.blood_group,
        date_of_birth: values.date_of_birth
          ? values.date_of_birth.format("YYYY-MM-DD")
          : null,

        user_type: "ADMIN",
        role_id: values.role_id,
      };

      // Add password only when creating a new user
      if (modalType === "add" && values.password) {
        userData.password = values.password;
      }

      if (modalType === "add") {
        setCreateLoading(true);
        request({
          url: `/api/admin/sub-admins`,
          method: "POST",
          data: userData,
          onSuccess: () => {
            ShowToast("User created successfully", Severty.SUCCESS);
            setImage(null); // ✅ explicitly null
            form.resetFields();
            setModalVisible(false);
            fetchUsers();
            setCreateLoading(false);
            setPhoneCountryIso(null);
          },
          onError: (error) => {
            console.log("error=====>", error);
            console.log("error_response=====>", error.response);
            ShowToast(
              error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setModalVisible(false);
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        request({
          url: `/api/admin/sub-admins/${selectedUser.id || selectedUser._id}`,
          method: "PUT",
          data: userData,
          onSuccess: () => {
            ShowToast("User updated successfully", Severty.SUCCESS);
            setModalVisible(false);
            fetchUsers();
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save user", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
    }
  };

  const resetUserModalState = () => {
    setModalVisible(false);
    form.resetFields();
    setCountryCode("");
    setPhoneCountry(null);
    setPhoneCountryIso(null);
    setImage(null);
  };

  const handleDeleteUser = async (user, reason) => {
    try {
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: true }));
      request({
        url: `/api/admin/sub-admins/${userId}`,
        method: "DELETE",
        data: { reason: reason },
        onSuccess: (data) => {
          console.log("data-=-=-=-=->", data);
          ShowToast("User deleted successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
          fetchUsers();
        },
        onError: (error) => {
          ShowToast(
            `${error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast(
        `${error.message ||
        error.response.data.message ||
        error.response.message ||
        "Failed to terminate session"
        }`,
        Severty.ERROR,
      );
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const resetDeleteModal = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const handleStatusToggle = async (user, newStatus) => {
    try {
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: true }));
      let params = new URLSearchParams();
      params.append("action", newStatus === true ? "active" : "inactive");
      request({
        url: `/api/admin/sub-admins/${userId}?${params.toString()}`,
        method: "DELETE",
        onSuccess: () => {
          ShowToast(
            `User ${newStatus ? "activated" : "suspended"} successfully`,
            Severty.SUCCESS,
          );
          fetchUsers();
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
        onError: (error) => {
          ShowToast(
            `${error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to update user status", Severty.ERROR);
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateUserPassword = async (values) => {
    try {
      setUpdateLoading(true);

      const payload = {
        admin_id: selectedUser?.id,
        new_password: values.password,
        confirm_new_password: values.confirm_password,
      };
      // Reset the password with the new password entered by the user
      await request({
        url: "/auth/change-password",
        method: "POST",
        data: payload,
        onSuccess: (data) => {
          ShowToast("Password has been changed successfully", Severty.SUCCESS);

          form2.resetFields();
          setChangePasswordModal(false);
          setUpdateLoading(false);
        },
        onError: (errorMessage) => {
          setUpdateLoading(false);
        },
      });
    } catch (error) {
      console.log("Error occurred while updating password", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleViewPermissions = (user) => {
    setLoading(true);
    if (!user?.role) {
      toast.warning("No role assigned to this user");
      setLoading(false);
      return;
    }
    request({
      url: `/api/admin/roles/${user.role?.id}`,
      method: "GET",
      onSuccess: (data) => {
        setSelectedRole(data?.data);
        setRoleModalVisible(true);
        setLoading(false);
      },
      onError: (error) => {
        ShowToast(error.response.data.message || "Failed to get role permissions", Severty.ERROR);
        setRoleModalVisible(false);
        setSelectedRole(null);
        setLoading(false);
      },
    });
  };

  const InfoRow = ({ label, value, children }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 12,
      }}
    >
      <Text type="secondary">{label}</Text>
      <Text strong>{children || value || "—"}</Text>
    </div>
  );

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "User ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code>USER_{id}</Text>,
    },
    {
      title: "Name",
      dataIndex: ["first_name", "middle_name", "last_name"],
      key: "name",
      width: 200,
      sorter: (a, b) => {
        const nameA = a?.first_name?.toLowerCase() + " " + a?.middle_name?.toLowerCase() + " " + a?.last_name?.toLowerCase() || "";
        const nameB = b?.first_name?.toLowerCase() + " " + b?.middle_name?.toLowerCase() + " " + b?.last_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar
            icon={<UserOutlined />}
            src={
              record?.profile_img ||
              record?.user?.profile_img ||
              record?.user?.avatar
            }
            style={{ backgroundColor: "#1890ff" }}
          />
          <span>{name || (record?.first_name ?? "") + " " + (record?.middle_name ?? "") + " " + (record?.last_name ?? "") || "N/A"}</span>
        </div>
      ),
    },
    // {
    //   title: lang("Email") || "Email",
    //   dataIndex: "email",
    //   key: "email",
    //   width: 200,
    //   render: (email) => (
    //     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    //       <span>{email || "N/A"}</span>
    //     </div>
    //   ),
    // },
    // {
    //   title: "Phone Number",
    //   dataIndex: "mobile",
    //   key: "mobile",
    //   width: 200,
    //   render: (mobile, record) => (
    //     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    //       <span>{`${record.country_code}-${mobile}` || "N/A"}</span>
    //     </div>
    //   ),
    // },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 220,
      render: (role, record) => (
        <Space>
          <Tag color="blue">{record?.role_name || "N/A"}</Tag>

          {record?.role_name && (
            <Tooltip title="View Role Permissions">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewPermissions(record)}
                loading={loading}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        const finalStatus =
          status || record.status || (record.isActive ? "Active" : "Deactivate");

        let color = "red";

        if (finalStatus === "Active" || finalStatus === "active") {
          color = "green";
        } else if (finalStatus === "Locked" || finalStatus === "locked") {
          color = "gold"; // 🔥 Yellow
        } else {
          color = "red";
        }

        return <Tag color={color}>{finalStatus}</Tag>;
      },
    },
    {
      title: "Registration Date",
      dataIndex: "join_date",
      key: "join_date",
      width: 120,
      sorter: (a, b) =>
        moment(a.join_date).valueOf() - moment(b.join_date).valueOf(),
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      width: 120,
      render: (last_login) => {
        return <span>{last_login ? moment(last_login).format("DD MMM YYYY, hh:mm A") : "-"}</span>;
      },
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={"Change Password"}>
            <Button
              className="button-action-small"
              icon={<Lock />}
              onClick={() => {
                setSelectedUser(record);
                setChangePasswordModal(true);
              }}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          {mapStatusToBoolean(record.status || record.isActive) ? (
            <Tooltip title={"Deactivate"}>
              <Button
                className="button-action-small"
                icon={<img src={SuspendIcon} alt="inactive" />}
                style={{ color: "#faad14" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    true,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          ) : (
            <Tooltip title={lang("Activate") || "Activate"}>
              <Button
                className="button-action-small"
                icon={<img src={CheckImgIcon} alt="activate" />}
                style={{ color: "#52c41a" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    false,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          )}
          <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              loading={deleteLoading[record._id || record.id]}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
              onClick={() => {
                setUserToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const PermissionTag = ({ label, enabled }) => (
    <Tag
      icon={enabled ? <CheckOutlined /> : <CloseOutlined />}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        fontSize: 13,
        borderRadius: 20,
        border: "none",
        fontWeight: 500,
        background: enabled
          ? "linear-gradient(135deg, #22c55e, #16a34a)"
          : "#e5e7eb",
        color: enabled ? "#fff" : "#6b7280",
      }}
    >
      {PERMISSION_ICONS[label]} {label}
    </Tag>
  );

  const showUserStatusConfirm = (
    record,
    isActive,
    handleStatusToggle,
    lang,
    statusToggleLoading,
    deleteLoading,
  ) => {
    const loading = statusToggleLoading[record._id || record.id];
    const disabled = loading || deleteLoading[record._id || record.id];

    confirm({
      title: isActive
        ? "Are you sure you want to deactivate this user?"
        : "Are you sure you want to activate this user?",
      okText: lang("Yes") || "Yes",
      cancelText: lang("No") || "No",
      okType: isActive ? "primary" : "primary",
      centered: true,
      okButtonProps: {
        loading,
        disabled,
      },
      onOk() {
        handleStatusToggle(record, !isActive);
      },
    });
  };

  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <TeamOutlined style={{ marginRight: 8 }} />
              {lang("Staff_Management") || "Staff Management"}
              <p>
                {lang(
                  "Admin_manages_staff_accounts_view_edit_roles_permissions_and_status",
                ) ||
                  "Admin manages staff accounts: view, edit, assign roles, manage permissions, and update status as necessary."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                icon={<FilterFilled />}
                onClick={() => setFiltersVisible((prev) => !prev)}
              >
                {filtersVisible ? "Hide Filters" : "Show Filters"}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddUser}
              >
                {lang("Add_Staff") || "Add Staff"}
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            {filtersVisible && (
              <>
                <Search
                  placeholder={
                    lang("Search_by_name_or_email") || "Search by name or email"
                  }
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  onSearch={handleSearch}
                  style={{ minWidth: 200, flex: "1 1 200px" }}
                />
                <Select
                  placeholder="Filter by Role"
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.role}
                  onChange={(value) => handleFilterChange("role", value)}
                  loading={rolesLoading}
                >
                  {roles.map((role) => (
                    <Select.Option key={role.id} value={role.id}>
                      {role.name}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  placeholder={lang("Filter_by_Status") || "Filter by Status"}
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                >
                  <Select.Option value={true}>{"Active"}</Select.Option>
                  <Select.Option value={false}>{"In Active"}</Select.Option>
                </Select>
                <Select
                  placeholder={"Sorting"}
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.sort}
                  onChange={(value) => handleFilterChange("sort", value)}
                >
                  <Select.Option value={"name"}>{"Name Wise"}</Select.Option>
                  <Select.Option value={"status"}>{"Status Wise"}</Select.Option>
                  <Select.Option value={"newest"}>{"Newest User"}</Select.Option>
                  <Select.Option value={"oldest"}>{"Oldest User"}</Select.Option>
                </Select>
                <RangePicker
                  placeholder={[
                    lang("Start_Date") || "Start Date",
                    lang("End_Date") || "End Date",
                  ]}
                  value={filters.dateRange}
                  onChange={(dates) => handleFilterChange("dateRange", dates)}
                />
                <Button
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => {
                    setFilters({
                      status: null,
                      role: null,
                      sort: null,
                      dateRange: null,
                    });
                    setSearchText("");
                    setPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                >
                  {lang("Clear_Filters") || "Clear Filters"}
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Users Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={Array.isArray(users) ? users : []}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${lang("of") || "of"
                } ${total} ${lang("users") || "users"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      <RolePermissionsModal
        open={roleModalVisible}
        role={selectedRole}
        onClose={() => {
          setRoleModalVisible(false);
          setSelectedRole(null);
        }}
      />

      {/* Add/Edit User Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            {modalType === "add"
              ? lang("Add_Staff") || "Add Staff"
              : lang("Edit_Staff") || "Edit Staff"}
          </div>
        }
        open={modalVisible && modalType !== "view"}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false)
        }
        footer={null}
        width={700}
        className="tab-modal"
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => handleFormSubmit(values)}
        >
          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item
                name="first_name"
                label={"First Name"}
                rules={[
                  {
                    required: true,
                    message: "Please enter user first name",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (value.trim() === "") {
                        return Promise.reject(
                          new Error("First Name cannot be empty or whitespace"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    max: 100,
                    message:
                      lang("Name_must_be_100_characters_or_less") ||
                      "First Name must be 100 characters or less",
                  },
                ]}
              >
                <Input placeholder={"Enter user first name"} />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="middle_name"
                label={"Middle Name"}
                rules={[
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                  {
                    max: 100,
                    message:
                      lang("Name_must_be_100_characters_or_less") ||
                      "Middle Name must be 100 characters or less",
                  },
                ]}
              >
                <Input placeholder={"Enter user middle name"} />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="last_name"
                label={"Last Name"}
                rules={[
                  {
                    required: true,
                    message: "Please enter user last name",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (value.trim() === "") {
                        return Promise.reject(
                          new Error("Last Name cannot be empty or whitespace"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    max: 100,
                    message:
                      lang("Name_must_be_100_characters_or_less") ||
                      "Last Name must be 100 characters or less",
                  },
                ]}
              >
                <Input placeholder={"Enter user last name"} />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="pronoun"
                label="Salutation"
                rules={[{ message: "Please select salutation" }]}
              >
                <Select
                  placeholder="Select salutation"
                  options={PRONOUN_OPTIONS}
                />
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item
                name="gender"
                label="Gender"
                rules={[{ message: "Please select gender" }]}
              >
                <Select
                  placeholder="Select gender"
                  options={GENDER_OPTIONS}
                  onChange={(val) => form.setFieldsValue({ gender: val })}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="role_id"
                label="Assigned Role"
                rules={[{ required: true, message: "Please select a role" }]}
              >
                <Select
                  placeholder="Select role"
                  loading={rolesLoading}
                  options={roles.map((r) => ({
                    label: r.name,
                    value: String(r.id ?? r._id),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="email"
                label={lang("Email") || "Email"}
                rules={[
                  { required: true, message: "Please enter email" },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      // Trim spaces
                      const email = value.trim();

                      // ❌ basic structure
                      if (!email.includes("@")) {
                        return Promise.reject(
                          new Error("Invalid email format"),
                        );
                      }

                      const [local, domain] = email.split("@");

                      // ❌ local part checks
                      if (
                        !local ||
                        local.startsWith(".") ||
                        local.endsWith(".")
                      ) {
                        return Promise.reject(
                          new Error("Invalid email local part"),
                        );
                      }

                      // ❌ domain checks
                      if (
                        !domain ||
                        domain.startsWith(".") ||
                        domain.endsWith(".") ||
                        domain.includes("..")
                      ) {
                        return Promise.reject(
                          new Error("Invalid email domain"),
                        );
                      }

                      // ❌ allowed characters (RFC-safe)
                      const emailRegex =
                        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,24}$/;

                      if (!emailRegex.test(email)) {
                        return Promise.reject(
                          new Error("Invalid email address"),
                        );
                      }

                      // ❌ TLD sanity check
                      const domainParts = domain.split(".");
                      const tld = domainParts[domainParts.length - 1];

                      if (tld.length < 2 || tld.length > 24) {
                        return Promise.reject(
                          new Error("Invalid email extension"),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={lang("Enter_email") || "Enter email"} />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="mobile"
                label="Mobile Number"
                // help={
                //   phoneCountryIso
                //     ? `Example: ${getPhoneExample(phoneCountryIso)}`
                //     : null
                // }
                rules={[
                  { required: true, message: "Please enter your phone number (8 to 10 digits)" },
                  () => ({
                    validator(_, value) {
                      if (!value || !phoneCountryIso) return Promise.resolve();

                      // remove non-digits
                      const digitsOnly = value.replace(/\D/g, "");
                      // const localNumber = digitsOnly.slice(
                      //   phoneCountry.dialCode.length,
                      // );

                      // // Minimum sanity check
                      // if (!/^\d+$/.test(localNumber)) {
                      //   return Promise.reject(
                      //     new Error("Phone number must contain only digits"),
                      //   );
                      // }

                      // // const expectedLength =
                      // //   getExpectedLocalLength(phoneCountry);

                      // const valid = isValidPhoneNumber(value, phoneCountryIso);

                      // return valid
                      //   ? Promise.resolve()
                      //   : Promise.reject(
                      //       new Error(
                      //         "Invalid phone number for selected country",
                      //       ),
                      //     );
                      // remove country code (NZ = 64)
                      const localNumber = digitsOnly.startsWith("64")
                        ? digitsOnly.slice(2)
                        : digitsOnly;

                      // ✅ Length validation (8 to 10 digits)
                      if (localNumber.length < 8 || localNumber.length > 10) {
                        return Promise.reject(
                          new Error("Phone number must be between 8 and 10 digits")
                        );
                      }

                      const phoneNumber = parsePhoneNumberFromString("+" + digitsOnly);

                      if (!phoneNumber || !phoneNumber.isValid()) {
                        return Promise.reject(
                          new Error("Invalid phone number for selected country")
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <PhoneInput
                  country="nz"
                  disableDropdown
                  onlyCountries={["nz"]}
                  value={form.getFieldValue("mobile")}
                  onChange={(value, country) => {
                    form.setFieldsValue({ mobile: value });
                    setCountryCode("64");
                    setPhoneCountryIso("NZ");
                    setPhoneCountry({ dialCode: "64" });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* {modalType === "add" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label={lang("Password") || "Password"}
                  rules={[
                    {
                      required: true,
                      message: "Please enter password",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        if (value.trim() === "") {
                          return Promise.reject(
                            new Error("Password cannot be empty or whitespace"),
                          );
                        }

                        const passwordRegex =
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

                        if (!passwordRegex.test(value)) {
                          return Promise.reject(
                            new Error(
                              "Password must contain at least 8 characters, including uppercase, lowercase, number and special character",
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.Password
                    placeholder={lang("Enter_password") || "Enter password"}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirm_password"
                  label={lang("Confirm_Password") || "Confirm Password"}
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    // prefix={<LockOutlined />}
                    placeholder={lang("Confirm_password") || "Confirm password"}
                  />
                </Form.Item>
              </Col>
            </Row>
          )} */}

          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item
                className=""
                label="Upload Image"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value !== undefined && value?.length > 0) {
                        return Promise.resolve();
                      }
                      // return Promise.reject(new Error(lang("Image is required")));
                      return Promise.resolve();
                    },
                  },
                ]}
                name="image"
              >
                <SingleImageUpload
                  value={image}
                  fileType={FileType}
                  imageType={"Profile"}
                  btnName={"Image"}
                  onChange={(data) => handleImage(data)}
                  isDimension={false}
                />
              </Form.Item>
              <p className="img-size-details">
                You can select only JPEG, JPG and PNG files upto 5 MB file size
              </p>
              {image && (
                <div
                  className="mt-2 add-img-product"
                  style={{ display: "flex", gap: 12, alignItems: "center" }}
                >
                  <Image width={120} src={image ?? notfound} />

                  {modalType === "edit" && (
                    <Button danger size="small" onClick={handleRemoveImage}>
                      Remove Image
                    </Button>
                  )}
                </div>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                name="date_of_birth"
                label="Date of Birth"
                rules={[
                  {
                    required: modalType === "add",
                    message: "Please select date of birth",
                  },
                  { validator: validateMinimumAge },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current > maxAllowedDob
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label={"Status"}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren={"Active"}
              unCheckedChildren={"In Activate"}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={resetUserModalState}
              disabled={createLoading || updateLoading}
            >
              {lang("Cancel") || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={modalType === "add" ? createLoading : updateLoading}
              disabled={createLoading || updateLoading}
            >
              {modalType === "add"
                ? lang("Create") || "Create"
                : lang("Update") || "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete User Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        key={userToDelete?.id || userToDelete?._id || "delete-modal"}
        onCancel={resetDeleteModal}
        onConfirm={(reason) => {
          handleDeleteUser(userToDelete, reason);
          resetDeleteModal();
        }}
        title={"Delete User"}
        message={"Are you sure you want to delete this user ?"}
        itemName={userToDelete?.name || userToDelete?.fullName}
        loading={
          userToDelete && deleteLoading[userToDelete._id || userToDelete.id]
        }
        okText={lang("Delete") || "Delete"}
        cancelText={lang("Cancel") || "Cancel"}
        input={true}
      />
      {/* View Staff Profile Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EyeOutlined style={{ color: "#1890ff" }} />
            {"Staff Profile"}
          </div>
        }
        open={modalType === "view" && modalVisible}
        onCancel={() => {
          if (!createLoading && !updateLoading) {
            resetUserModalState();
          }
        }}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            {lang("Close") || "Close"}
          </Button>,

          selectedUser?.role && (
            <Button
              key="permissions"
              icon={<SafetyOutlined />}
              onClick={() => handleViewPermissions(selectedUser)}
              loading={loading}
            >
              View Permissions
            </Button>
          ),
          selectedUser && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                setModalVisible(false); // close view first
                setTimeout(() => {
                  handleEditUser(selectedUser);
                }, 0);
              }}
              disabled={createLoading || updateLoading}
            >
              {lang("Edit") || "Edit"}
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedUser && (
          <div>
            {/* ===== Header / Hero ===== */}
            <Card
              style={{
                marginBottom: 24,
                borderRadius: 16,
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "#fff",
              }}
              bodyStyle={{ padding: 28 }}
            >
              <Row align="middle" justify="space-between">
                <Col>
                  <Space size={16}>
                    <Avatar
                      size={72}
                      src={selectedUser.profile_img}
                      icon={<UserOutlined />}
                      style={{
                        background: "#2563eb",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                      }}
                    />
                    <div>
                      <Title level={3} style={{ color: "#fff", margin: 0 }}>
                        {(selectedUser.first_name ?? "") + " " + (selectedUser.middle_name ?? "") + " " + (selectedUser.last_name ?? "")}
                      </Title>
                      {/* <Text style={{ color: "rgba(255,255,255,0.7)" }}>
                        {selectedUser.email}
                      </Text> */}
                      <div style={{ marginTop: 8 }}>
                        <Tag color="blue">
                          {selectedUser.role_name || "No Role"}
                        </Tag>
                        <Tag
                          color={
                            mapStatusToBoolean(selectedUser.status)
                              ? "green"
                              : "red"
                          }
                        >
                          {selectedUser.status}
                        </Tag>
                      </div>
                    </div>
                  </Space>
                </Col>

                <Col>
                  <Text style={{ color: "rgba(255,255,255,0.6)" }}>
                    USER_{selectedUser.id}
                  </Text>
                </Col>
              </Row>
            </Card>

            {/* ===== Details Grid ===== */}
            <Row gutter={[24, 24]}>
              {/* Personal Info */}
              <Col span={24} md={12}>
                <Card
                  title="Personal Information"
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                  }}
                >
                  <InfoRow label="Pronoun" value={selectedUser.pronoun} />
                  <InfoRow label="Gender" value={selectedUser.gender} />

                  <InfoRow
                    label="Date of Birth"
                    value={
                      selectedUser.date_of_birth
                        ? moment(selectedUser.date_of_birth).format("DD MMM YYYY")
                        : "—"
                    }
                  />

                  {/* <InfoRow
                    label="Age"
                    value={
                      selectedUser.date_of_birth
                        ? moment().diff(moment(selectedUser.date_of_birth), "years")
                        : "—"
                    }
                  /> */}
                </Card>
              </Col>

              {/* Contact Info */}
              <Col span={24} md={12}>
                <Card
                  title="Account Information"
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                  }}
                >
                  <InfoRow label="Phone">
                    {`${selectedUser.country_code || ""} ${selectedUser.mobile || ""
                      }`}
                  </InfoRow>

                  <InfoRow
                    label="Joining Date"
                    value={moment(selectedUser.join_date).format("DD MMM YYYY")}
                  />

                  <InfoRow
                    label="Last Login"
                    value={
                      selectedUser.last_login
                        ? moment(selectedUser.last_login).format(
                          "DD MMM YYYY, hh:mm A",
                        )
                        : "Never"
                    }
                  />
                  <InfoRow
                    label="Email"
                    value={selectedUser.email}
                  />
                </Card>
              </Col>
            </Row>
            <Card
              size="small"
              title="Role & Permissions"
              style={{
                background: "linear-gradient(135deg, #020617, #0f172a)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 12,
                marginTop: 24,
              }}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Text style={{ color: "#94a3b8", fontSize: 13 }}>
                  Assigned Role
                </Text>

                <Space align="center" size="middle">
                  <Tag
                    color="blue"
                    style={{
                      fontSize: 14,
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontWeight: 500,
                    }}
                  >
                    {selectedUser?.role_name || "N/A"}
                  </Tag>

                  <Tooltip title="View role permissions">
                    <Button
                      shape="circle"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewPermissions(selectedUser)}
                      loading={loading}
                      style={{
                        background: "#0ea5e9",
                        color: "#fff",
                        border: "none",
                        boxShadow: "0 6px 16px rgba(14,165,233,0.35)",
                      }}
                    />
                  </Tooltip>
                </Space>
              </Space>
            </Card>
          </div>
        )}
      </Modal>

      {/* Update Password Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            Update User Password
          </div>
        }
        open={changePasswordModal}
        onCancel={() => setChangePasswordModal(false)}
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form2}
          layout="vertical"
          onFinish={(values) => handleUpdateUserPassword(values)}
        >
          <Row gutter={16} className="mb-3" >
            <Col span={24} md={12}>
              <Form.Item
                name="password"
                label={lang("Password") || "Password"}
                rules={[
                  {
                    required: true,
                    message: "Please enter password",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      if (value.trim() === "") {
                        return Promise.reject(
                          new Error("Password cannot be empty or whitespace"),
                        );
                      }

                      const passwordRegex =
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

                      if (!passwordRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Password must contain at least 8 characters, including uppercase, lowercase, number and special character",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Enter_password") || "Enter password"}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item
                name="confirm_password"
                label={lang("Confirm_Password") || "Confirm Password"}
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Confirm_password") || "Confirm password"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                form2.resetFields();
                setChangePasswordModal(false);
              }}
              disabled={createLoading || updateLoading}
            >
              {lang("Cancel") || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateLoading}
              disabled={updateLoading}
            >
              {updateLoading ? "Updating..." : "Update Password"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SubAdminManager;
